import { ReactNode, useRef } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";
import { cn } from "@/lib/utils";

type AnimationType = "fade-up" | "fade-in-scale" | "slide-up";

interface AnimateOnScrollProps {
  children: ReactNode;
  animation?: AnimationType;
  delay?: number;
  className?: string;
  threshold?: number;
  triggerOnce?: boolean;
}

export function AnimateOnScroll({
  children,
  animation = "fade-up",
  delay = 0,
  className,
  threshold = 0.1,
  triggerOnce = true,
}: AnimateOnScrollProps) {
  const { ref, isVisible } = useScrollAnimation({ threshold, triggerOnce });
  const initiallyVisible = useRef(isVisible);

  // Mobile fast-path: if visible on first render, skip animation entirely
  if (initiallyVisible.current) {
    return <div ref={ref} className={className}>{children}</div>;
  }

  const animationClass = {
    "fade-up": "animate-fade-up",
    "fade-in-scale": "animate-fade-in-scale",
    "slide-up": "animate-slide-up",
  }[animation];

  return (
    <div
      ref={ref}
      className={cn(isVisible ? animationClass : "opacity-0", className)}
      style={isVisible && delay ? { animationDelay: `${delay}ms` } : undefined}
    >
      {children}
    </div>
  );
}
